import { useState, useEffect } from "react";
import {
  Col,
  Row,
  Space,
  Typography,
  Image,
  Form,
  App,
  Upload,
  Result
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BTN_TXT,
  DESC_TXT,
  GLOBAL_CONSTANTS,
  TITLE_TXT,
  SCF_TEMP_COL,
  SCF_TEMP_DATA,
  GEN_TEXT,
} from "../../../constants/constants";
import CustomButton from "../../../components/CustomButton/Button";
import CustomInputNumber from "../../../components/CustomInputNumber/InputNumber"
import { getSuppliers } from "../../../redux/slices/supplierSlice";
import SupplyChainFormatTable from "./SupplyChainFormatTable";
import SupplierAllocation from "./SupplierAllocation";
import UploadIcon from "../../../assets/icons/upload-icon.svg";
import SuccessImg from "../../../assets/images/success.png"

import Excel from "exceljs";
import { saveAs } from "file-saver";
import { utils, readFile } from "xlsx";
import {
  formatDateTime,
  handleUploadFile,
  parseUnknownDate,
  resolveMaxFundRequiredPct,
  maxFundAllowedAmount,
} from "../../../helpers/helpers";
import { DEAL_CATEGORY_IDS } from "../../../helpers/dealCategoryProducts";
import { scfUpload, scfValidate, updateErrorStatus, updateSuccessStatus } from "../../../redux/slices/InvoiceuploadSlice";
import CustomModal from "../../../components/CustomModal/CustomModal"
import FormFooter from "../../../components/CustomForms/FormFooter";
import DealDetailNoSpace from "../../../components/Details/DealDetailNoSpace"
import Step2 from "./Step2";
import Step3 from "./Step3";
import { count } from "../../../redux/slices/AuthSlice";

import { DownloadOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const workSheetName = "Supply_Chain_Template";

const UploadFiles = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const location = useLocation();
  const { selectedBusEntId, selectedEnterprise, selectedEnt, selectedOrg, tenure } = location.state || {};
  const enterprise = selectedEnterprise || selectedEnt;
  const dealCategoryId = location.state?.dealCategoryId || location.state?.dealType || DEAL_CATEGORY_IDS.SUPPLY_CHAIN_FINANCE;
  const maxFundRequiredPct = resolveMaxFundRequiredPct(enterprise?.maxFundRequiredPct);
  const { user } = useSelector((state) => state?.auth || {});
  const { success, error, validateLoading, invoiceSubmitLoading } = useSelector(
    (state) => state?.invoiceUpload || {}
  );
  
  const { suppliersList } = useSelector((state) => state.supplier);
  const supplierOptions = suppliersList?.map((s) => ({
    label: s.supplierName,
    value: s.supplierId,
  })) || [];

  // Org-scoped via /api/business/suppliers (req.orgId); high limit avoids Admin-style truncation
  useEffect(() => {
    dispatch(getSuppliers({ status: "ACTIVE", page: 1, limit: 1000 }));
  }, [dispatch]);

  const [financingType, setFinancingType] = useState("LOB1000001");
  const [invoiceData, setInvoiceData] = useState({
    invNum: [],
    invDate: [],
    invDueDate: [],
    invAmt: [],
    matCost: [],
    totalInvValue: null,
    totalMaterialCost: null,
    fundRequired: null,
    invFileUrl: null,
    invZipFileUrl: null,
  });
  const [sheetRows, setSheetRows] = useState([]);
  const [maxAllowableTenure, setMaxAllowableTenure] = useState(null);
  const [lockedDueDate, setLockedDueDate] = useState(null);
  const [isTempDownloading, setTempDownloading] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const workbook = new Excel.Workbook();

  const watchedAllocations = Form.useWatch("allocations", form) || {};
  const watchedSupplierIds = Form.useWatch("supplierIds", form) || [];
  const watchedFundReq = Form.useWatch("fundReq", form);

  const totalAllocatedAmount = watchedSupplierIds.reduce((sum, id) => {
    return sum + Number(watchedAllocations[id]?.amount || 0);
  }, 0);

  const isAllocationExceeded =
    watchedSupplierIds.length > 0 &&
    watchedFundReq &&
    Number((totalAllocatedAmount - Number(watchedFundReq)).toFixed(2)) > 0;

  const hasIncompleteAllocation =
    watchedSupplierIds.length > 0 &&
    watchedSupplierIds.some((id) => {
      const amt = Number(watchedAllocations[id]?.amount || 0);
      return amt <= 0;
    });

  const isSubmitDisabled =
    !invoiceData.totalInvValue ||
    !invoiceData.fundRequired ||
    !invoiceData.invFileUrl ||
    !invoiceData.invZipFileUrl ||
    !watchedSupplierIds ||
    watchedSupplierIds.length === 0 ||
    hasIncompleteAllocation ||
    isAllocationExceeded;

  useEffect(() => {
    if (success) {
      message.success("Summary file validated successfully");
      dispatch(updateSuccessStatus());
    }

    if (error) {
      const errorMessage =
        (error && (error.message || error.response?.message || error)) ||
        "Summary file validation failed";
      message.error(errorMessage);
      dispatch(updateErrorStatus());
    }

    return () => {
      dispatch(updateErrorStatus());
      dispatch(updateSuccessStatus());
    };
  }, [dispatch, message, success, error]);

  const handleDownloadTemp = async () => {
    setTempDownloading(true);
    try {
      const worksheet = workbook.addWorksheet(workSheetName);
      worksheet.columns = SCF_TEMP_COL;
      worksheet.getRow(1).font = { bold: true };

      worksheet.columns.forEach((column) => {
        column.width = column.header.length + 5;
        column.alignment = { horizontal: "center" };
      });

      SCF_TEMP_DATA.forEach((row) => worksheet.addRow(row));

      worksheet.eachRow({ includeEmpty: false }, (row) => {
        row._cells.forEach((cell) => {
          worksheet.getCell(cell._address).border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      const buf = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buf]), `${workSheetName}.xlsx`);
    } catch (error) {
      message.error(`Excel Download Error: ${error}`);
    } finally {
      workbook.removeWorksheet(workSheetName);
      setTempDownloading(false);
    }
  };

  const handleUploadChange = (info) => {
    if (info.file.status !== "error") {
      const originalFile = info.file.originFileObj;
      const formattedTime = formatDateTime();
      const newName = `SCF_${formattedTime}_${originalFile.name}`;

      // update file name in UI
      info.file.name = newName;
    }
  };

  const handleBeforeUpload = (file) => {
    const isExcel =
      file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel";

    if (!isExcel) {
      message.error("You can only upload Excel files!");
      return Upload.LIST_IGNORE;
    }

    const isLt2MB = file.size / 1024 / 1024 < 20;
    if (!isLt2MB) {
      message.error("File must be smaller than 20MB!");
      return Upload.LIST_IGNORE;
    }

    return true; // Allow upload
  };

  const handleCustomRequest = async ({ file, onSuccess, onError }) => {
    const formattedTime = formatDateTime();
    const safeName = file.name.trim().replace(/\s+/g, "_");

    const renamedFile = new File(
      [file],
      `SCF_${formattedTime}_${safeName}`,
      {
        type: file.type,
      }
    );

    // Clear old data when new file is uploaded
    setSheetRows([]);

    setInvoiceData((prev) => ({
      ...prev,
      invNum: [],
      invDate: [],
      invDueDate: [],
      invAmt: [],
      matCost: [],
      totalInvValue: null,
      totalMaterialCost: null,
      fundRequired: null,
      invFileUrl: null,
    }));

    form.setFieldsValue({
      totalInvValue: undefined,
      totalMaterialCost: undefined,
      fundReq: undefined,
      tenure: undefined,
    });

    const readSheetPromise = new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const wb = readFile(event.target.result);
          const sheets = wb.SheetNames;
          const options = { dateNF: "dd-MM-yyyy", raw: false };

          if (sheets.length) {
            const rows = utils.sheet_to_json(wb.Sheets[sheets[0]], options);
            resolve(rows);
            return;
          }
          resolve([]);
        } catch (e) {
          resolve([]);
        }
      };
      reader.onerror = () => resolve([]);
      reader.readAsArrayBuffer(file);
    });

    try {
      const path = `scf_docs/${selectedBusEntId}`;
      const [{ fileUrl }, rows] = await Promise.all([
        handleUploadFile(renamedFile, path),
        readSheetPromise,
      ]);

      const validRows = rows || [];
      setSheetRows(validRows);

      setInvoiceData((prev) => ({
        ...prev,
        invFileUrl: fileUrl,
      }));

      // Success snackbar
      message.success("Summary file uploaded successfully");

      onSuccess("ok");

      if (validRows.length > 0) {
        handleValidateInvoice(validRows, fileUrl);
      }
    } catch (err) {
      message.error("Failed to upload summary file");
      onError(err);
    }
  };

  const handleRemoveFile = () => {
    setSheetRows([]);

    setInvoiceData((prev) => ({
      ...prev,
      invNum: [],
      invDate: [],
      invDueDate: [],
      invAmt: [],
      matCost: [],
      totalInvValue: null,
      totalMaterialCost: null,
      fundRequired: null,
      invFileUrl: null,
    }));

    form.setFieldsValue({
      totalInvValue: undefined,
      totalMaterialCost: undefined,
      fundReq: undefined,
    });

    dispatch(updateErrorStatus());
    dispatch(updateSuccessStatus());

    return true;
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".xlsx,.xls",
    maxCount: 1,
    beforeUpload: handleBeforeUpload,
    customRequest: handleCustomRequest,
    onChange: handleUploadChange,
    onRemove: handleRemoveFile,
  };

  const handleValidateInvoice = (values, uploadedUrl) => {
    let invNum = [],
      totInvVal = 0,
      totMatCost = 0;
    let invAmt = [],
      matCost = [],
      invDate = [],
      invDueDate = [];

    let hasError = false; // error flag

    const expectedColumns = [
      "PO Number",
      "PO Date",
      "PO Amount",
      "Material Cost",
      "PO Due Date",
    ];

    const currentRows = (Array.isArray(values) && values.length > 0) ? values : sheetRows;

    if (currentRows.length > 0) {
      const uploadedColumns = Object.keys(currentRows[0] || {});

      const isValidTemplate =
        expectedColumns.length === uploadedColumns.length &&
        expectedColumns.every((col) => uploadedColumns.includes(col));

      if (!isValidTemplate) {
        message.error(
          "Invalid file format. Please upload the downloaded document template only."
        );
        return;
      }
    }

    if (currentRows.length) {
      for (const obj of currentRows) {
        const formattedObj = {};
        if (!Object.keys(obj).some((k) => obj[k])) {
          message.error("Uploaded file is not valid");
          hasError = true;
          break;
        }

        Object.entries(obj).forEach(([k, v]) => {
          const keyLower = String(k || "").trim().toLowerCase();
          if (keyLower === "po number" || keyLower === "purchase order number" || keyLower === "po no" || keyLower === "purchase order no") formattedObj.invoiceNumber = v;
          if (keyLower === "po date" || keyLower === "purchase order date") formattedObj.invoiceDate = v;
          if (keyLower === "po amount" || keyLower === "purchase order amount" || keyLower === "po value" || keyLower === "purchase order value") formattedObj.invoiceAmount = v;
          if (keyLower === "material cost" || keyLower === "raw material value" || keyLower === "raw material cost" || keyLower === "material value") formattedObj.materialCost = v;
          if (keyLower === "po due date" || keyLower === "purchase order due date") formattedObj.invoiceDueDate = v;
        });

        const { invoiceNumber, invoiceAmount, invoiceDate, invoiceDueDate, materialCost } =
          formattedObj;

        const newinvoiceDate = parseUnknownDate(invoiceDate);
        const newinvoiceDueDate = parseUnknownDate(invoiceDueDate);

        if (new Date(newinvoiceDueDate) < new Date(newinvoiceDate)) {
          message.error("Document Due Date must be greater than Document Date");
          hasError = true;
          break;
        }

        const todayCalendarStr = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0];

        if (newinvoiceDate > todayCalendarStr) {
          message.error("Document Date should not be in the future");
          hasError = true;
          break;
        }

        if (Number(materialCost) >= Number(invoiceAmount)) {
          message.error("Material Cost must be less than the Total PO Value.");
          hasError = true;
          break;
        }

        totInvVal += Number(invoiceAmount);
        totMatCost += Number(materialCost);
        invNum.push(invoiceNumber);
        invDate.push(newinvoiceDate);
        invDueDate.push(newinvoiceDueDate);
        invAmt.push(invoiceAmount);
        matCost.push(Number(materialCost));
      }

      if (hasError) return; // prevent API call

      const duplicates = invNum.filter((num, idx) => invNum.indexOf(num) !== idx);
      if (duplicates.length > 0) {
        message.error(`Duplicate PO numbers found in the uploaded file: ${[...new Set(duplicates)].join(", ")}`);
        return;
      }

      if (invDueDate.length > 1) {
        const timestamps = invDueDate.map(d => {
          const [y, m, day] = d.split('-').map(Number);
          return Date.UTC(y, m - 1, day);
        });
        const minDueDate = Math.min(...timestamps);
        const maxDueDate = Math.max(...timestamps);
        const diffDays = Math.round((maxDueDate - minDueDate) / (1000 * 3600 * 24));
        if (diffDays > 7) {
          message.error("PO due dates cannot vary by more than 7 days between the earliest and latest date");
          return;
        }
      }

      if (totMatCost >= totInvVal) {
        message.error("Material Cost must be less than the Total PO Value.");
        return;
      }

      const todayCalendarDateStr = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0];
      const [tY, tM, tD] = todayCalendarDateStr.split("-").map(Number);
      const todayUtc = Date.UTC(tY, tM - 1, tD);

      const remainingTenures = invDueDate.map(d => {
        const [y, m, day] = d.split("-").map(Number);
        const dueUtc = Date.UTC(y, m - 1, day);
        return Math.round((dueUtc - todayUtc) / (1000 * 3600 * 24));
      });

      if (remainingTenures.some(t => t < 30)) {
        message.error("The deal cannot be uploaded because the remaining tenure is less than the minimum required tenure of 30 days.");
        return;
      }

      const calcMaxTenure = remainingTenures.length === 1
        ? remainingTenures[0]
        : Math.round(remainingTenures.reduce((a, b) => a + b, 0) / remainingTenures.length);

      setMaxAllowableTenure(calcMaxTenure);
      setLockedDueDate(invDueDate.length === 1 ? invDueDate[0] : `${invDueDate[0]} to ${invDueDate[invDueDate.length - 1]}`);

      // Auto-calc max fund from credit-limit % (integer math avoids float floor bugs, e.g. 700000*0.7)
      const autoFundReq = maxFundAllowedAmount(totMatCost, maxFundRequiredPct);
      const userEnteredFundReq = form.getFieldValue("fundReq");
      const finalFundReq =
        userEnteredFundReq !== undefined &&
        userEnteredFundReq !== null &&
        String(userEnteredFundReq).trim() !== ""
          ? Number(userEnteredFundReq)
          : autoFundReq;

      const userEnteredTenure = form.getFieldValue("tenure");
      const finalTenure =
        userEnteredTenure !== undefined &&
        userEnteredTenure !== null &&
        String(userEnteredTenure).trim() !== ""
          ? Number(userEnteredTenure)
          : calcMaxTenure;

      form.setFieldsValue({
        totalInvValue: totInvVal,
        totalMaterialCost: totMatCost,
        fundReq: finalFundReq,
        tenure: finalTenure,
      });

      setInvoiceData((prev) => ({
        ...prev,
        ...(uploadedUrl ? { invFileUrl: uploadedUrl } : {}),
        invNum,
        invDate,
        invDueDate,
        invAmt,
        matCost,
        totalInvValue: totInvVal,
        totalMaterialCost: totMatCost,
        fundRequired: finalFundReq,
      }));

      const payload = {
        busEntId: selectedBusEntId,
        invoiceNumber: invNum,
        invoiceDate: invDate,
        invoiceAmt: invAmt,
        materialCost: matCost,
        invoiceDueDate: invDueDate,
        fundRequired: finalFundReq,
        dealCategoryId,
      };

      dispatch(scfValidate(payload));
    }
  };

  const handleInvoicesSubmit = async () => {
    try {
      const supplierIds = form.getFieldValue("supplierIds") || [];
      if (!supplierIds || supplierIds.length === 0) {
        form.setFields([
          {
            name: "supplierIds",
            errors: ["Please select at least one supplier before submitting the Purchase Order"],
          },
        ]);
        message.error("Please select at least one supplier before submitting the Purchase Order");
        return;
      }

      if (Number(invoiceData.totalMaterialCost) >= Number(invoiceData.totalInvValue)) {
        message.error("Material Cost must be less than the Total PO Value.");
        return;
      }

      await form.validateFields();

      const fundReq = form.getFieldValue("fundReq");
      const allocations = form.getFieldValue("allocations") || {};

      const totalAlloc = supplierIds.reduce((sum, id) => {
        return sum + Number(allocations[id]?.amount || 0);
      }, 0);
      if (Number((totalAlloc - fundReq).toFixed(2)) > 0) {
        message.error("Supplier allocation breakdown cannot exceed funding amount");
        return;
      }

      const supplierAllocations = supplierIds.map(id => {
        const amount = Number(allocations[id]?.amount || 0);
        return {
          supplierId: id,
          amount
        };
      });

      const formTenure = form.getFieldValue("tenure") || maxAllowableTenure || tenure;
      const payload = {
        busEntId: selectedBusEntId,
        tenure: Number(formTenure),
        totalInvoiceValue: invoiceData.totalInvValue,
        totalMaterialCost: invoiceData.totalMaterialCost,
        fundRequired: fundReq,
        lineOfBusId: financingType,
        dealCategoryId,
        supplierAllocations,
        uploadFiles: {
          invoicesummary: {
            invoiceNumber: invoiceData.invNum,
            invoiceDate: invoiceData.invDate,
            invoiceAmt: invoiceData.invAmt,
            invoiceDueDate: invoiceData.invDueDate,
            docUrl: invoiceData.invFileUrl,
          },
          invoiceZipFile: {
            docUrl: invoiceData.invZipFileUrl,
          },
        },
      };

      const result = await dispatch(scfUpload(payload));
      if (result?.payload?.statusCode === 201) {
        setSuccessModalOpen(true);
        setTimeout(() => {
          navigate("/deals", {
            state: { activeTab: "supply-chain-deals" },
          });
        }, 2000);
        dispatch(count({ type: "DEALS", dealCategoryId }));
      }
    } catch (error) {
      return;
    }
  };

  return (
    <div className="upload-files-container">
      <Title level={3} style={{ margin: 0, fontSize: 24 }}>
        {TITLE_TXT.PO_UPLOAD}
      </Title>

      <div className="bus-ent-details-container">
        <Row gutter={[0, 16]} justify="space-between">
          <Col>
            <Space direction="vertical" size="middle">
              <DealDetailNoSpace
                label={GEN_TEXT.INVO_FOR_ENT}
                value={enterprise?.entName || GLOBAL_CONSTANTS.NA}
              />
              <DealDetailNoSpace
                label={GEN_TEXT.INVO_FOR_BUS}
                value={selectedOrg?.orgName || user?.orgName || GLOBAL_CONSTANTS.NA}
              />
            </Space>
          </Col>
          <Col>
            <Space direction="vertical" size="middle">
              <DealDetailNoSpace
                label="PO Due Date"
                value={lockedDueDate || GLOBAL_CONSTANTS.NA}
              />
              <DealDetailNoSpace
                label="Maximum Allowable Tenure"
                value={maxAllowableTenure ? `${maxAllowableTenure} Days` : (tenure ? `${tenure} Days` : GLOBAL_CONSTANTS.NA)}
              />
            </Space>
          </Col>
        </Row>
      </div>

      <Row gutter={[0, 16]} style={{ marginTop: 30 }} justify="space-between">
        <Col>
          <Space direction="vertical" size="small">
            <Title level={5} style={{ margin: 0 }}>
              {TITLE_TXT.PO_UPLOAD_STEP_1}
            </Title>
            <Text style={{ color: "#40566D", fontSize: 14 }}>
              {DESC_TXT.PO_UPLOAD_STEP_1}
            </Text>
          </Space>
        </Col>
        <Col>
          <CustomButton
            type="primary"
            style={{ padding: 24 }}
            onClick={handleDownloadTemp}
            loading={isTempDownloading}
          >
            Download Summary Template <DownloadOutlined style={{ marginLeft: 8 }} />
          </CustomButton>
        </Col>
      </Row>

      <SupplyChainFormatTable data={sheetRows} />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          // totalInvValue: 0,
          // fundReq: 0,
        }}
        className="invoice-upload-form"
        style={{ marginTop: 40 }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12} xl={{ flex: "1" }}>
            <Form.Item>
              <Upload {...uploadProps} style={{ width: "100%" }}>
                <CustomButton
                  color="primary"
                  variant="outlined"
                  block
                  icon={
                    <Image preview={false} src={UploadIcon} alt="upload" />
                  }
                  className="upload-validate-btn"
                >
                  {BTN_TXT.UPLOAD_SUM_FILE}
                </CustomButton>
              </Upload>
            </Form.Item>
          </Col>
          <Col xs={24} md={12} xl={{ flex: "1" }}>
            <Form.Item>
              <CustomButton
                color="danger"
                variant="outlined"
                htmlType="button"
                onClick={handleValidateInvoice}
                loading={validateLoading}
                disabled={!invoiceData.invFileUrl}
                block
                className="upload-validate-btn"
              >
                {BTN_TXT.VALIDATE_SUM}
              </CustomButton>
            </Form.Item>
          </Col>

          <Col xs={24} md={8} xl={{ flex: "1" }}>
            <CustomInputNumber
              label="Total PO Value"
              name="totalInvValue"
              controls={false}
              disabled
            />
          </Col>
          <Col xs={24} md={8} xl={{ flex: "1" }}>
            <CustomInputNumber
              label="Total Material Cost"
              name="totalMaterialCost"
              controls={false}
              disabled
            />
          </Col>
          <Col xs={24} md={8} xl={{ flex: "1" }}>
            <CustomInputNumber
              label="Funding Amount"
              placeholder={`Max ${parseFloat((resolveMaxFundRequiredPct(maxFundRequiredPct) * 100).toFixed(2))}% of material cost`}
              name="fundReq"
              controls={false}
              rules={[
                {
                  validator(_, value) {
                    const maxAllowed = maxFundAllowedAmount(invoiceData.totalMaterialCost || 0, maxFundRequiredPct);
                    const pctLabel = parseFloat((resolveMaxFundRequiredPct(maxFundRequiredPct) * 100).toFixed(2));
                    if (value === undefined || value === null || value === "") return Promise.resolve();
                    if (!/^\d+$/.test(String(value).trim())) return Promise.reject("Only numbers are allowed");
                    const numericValue = Number(String(value).trim());
                    if (numericValue < 80000) return Promise.reject("Minimum funding amount is 80,000");
                    if (numericValue > maxAllowed) return Promise.reject(`Cannot exceed ${pctLabel}% (Max Investment Percentage) of Total Material Cost (₹${maxAllowed})`);

                    const allocations = form.getFieldValue("allocations") || {};
                    const supplierIds = form.getFieldValue("supplierIds") || [];
                    if (supplierIds.length > 0) {
                      const totalAlloc = supplierIds.reduce((sum, id) => {
                        return sum + Number(allocations[id]?.amount || 0);
                      }, 0);
                      if (Number((totalAlloc - numericValue).toFixed(2)) > 0) {
                        return Promise.reject("Supplier allocation breakdown cannot exceed funding amount");
                      }
                    }

                    return Promise.resolve();
                  }
                }
              ]}
            />
          </Col>
          <Col xs={24} md={8} xl={{ flex: "1" }}>
            <CustomInputNumber
              label={`Tenure (Max: ${maxAllowableTenure ? maxAllowableTenure + " Days" : "Auto-calc"})`}
              name="tenure"
              controls={false}
              disabled={true}
              placeholder="Auto-calculated Tenure"
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === "") return Promise.resolve();
                    const num = Number(value);
                    if (num < 30) {
                      return Promise.reject("The deal cannot be uploaded because the remaining tenure is less than the minimum required tenure of 30 days.");
                    }
                    if (maxAllowableTenure && num > maxAllowableTenure) {
                      return Promise.reject("Tenure cannot exceed the maximum allowable period based on the invoice due date.");
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            />
          </Col>
        </Row>
        <SupplierAllocation supplierOptions={supplierOptions} form={form} />
      </Form>

      <Step2 selectedBusEntId={selectedBusEntId} setInvoiceData={setInvoiceData} handleUploadChange={handleUploadChange} />
      <Step3 financingType={financingType} setFinancingType={setFinancingType} />
      <FormFooter
        onCancelClick={() => {
          navigate(-1);
          form.resetFields();
        }}
        onSubmitClick={handleInvoicesSubmit}
        btnType="invoice"
        disabled={isSubmitDisabled}
        submitLoading={invoiceSubmitLoading}
        btnName={BTN_TXT.SUBMIT}
        error={error}
      />

      {successModalOpen && (
        <CustomModal
          open={successModalOpen}
          onCancel={() => setSuccessModalOpen(false)}
          centered
          content={
            <>
              <Result
                icon={<Image src={SuccessImg} preview={false} alt="Success" />}
                title={<Title level={4}>{TITLE_TXT.DOC_SUB_SUC}</Title>}
                subTitle={<Text style={{ color: "#40566D", fontSize: 14 }}>{DESC_TXT.PO_DOC_SUB_SUC || DESC_TXT.DOC_SUB_SUC}</Text>}
              />
            </>
          }
        />
      )}
    </div>
  );
};

export default UploadFiles;



